import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Building2, Users, Hash, Settings, Plus, Pencil, Trash2, Loader2,
  Search, Save, ChevronRight, ToggleLeft, Globe, Calendar, AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FEATURE_GROUPS = [
  { label: 'Core Access', features: [
    { key: 'stack_map', label: 'Tech Stack Map', plan: 'starter' },
    { key: 'tech_database', label: 'Tech Database', plan: 'starter' },
    { key: 'property_dashboard', label: 'Property Dashboard', plan: 'starter' },
  ]},
  { label: 'Reports', features: [
    { key: 'basic_reports', label: 'Basic Reports', plan: 'starter' },
    { key: 'integration_reports', label: 'Integration Reports', plan: 'starter' },
    { key: 'vendor_reports', label: 'Vendor Reports', plan: 'starter' },
    { key: 'advanced_reports', label: 'Advanced Reports', plan: 'professional' },
    { key: 'brand_reports', label: 'Brand Reports', plan: 'professional' },
    { key: 'cost_reports', label: 'Cost & Financial Reports', plan: 'professional' },
    { key: 'executive_reports', label: 'Executive Reports', plan: 'enterprise' },
    { key: 'risk_reports', label: 'Risk Reports', plan: 'enterprise' },
  ]},
  { label: 'Operations', features: [
    { key: 'governance', label: 'Governance Suite', plan: 'professional' },
    { key: 'compliance', label: 'Compliance Tracking', plan: 'professional' },
    { key: 'contracts', label: 'Contract Management', plan: 'professional' },
    { key: 'change_management', label: 'Change Management', plan: 'enterprise' },
    { key: 'corporate_stack', label: 'Corporate Stack Push', plan: 'professional' },
  ]},
  { label: 'Collaboration', features: [
    { key: 'vendor_portal', label: 'Vendor Portal Access', plan: 'enterprise' },
    { key: 'brand_portal', label: 'Brand Portal Access', plan: 'enterprise' },
    { key: 'rfp_management', label: 'RFP Management', plan: 'enterprise' },
  ]},
  { label: 'Data & Export', features: [
    { key: 'export_pdf', label: 'PDF Export', plan: 'starter' },
    { key: 'export_csv', label: 'CSV Export', plan: 'professional' },
    { key: 'export_email', label: 'Email Reports', plan: 'professional' },
    { key: 'scheduled_reports', label: 'Scheduled Reports', plan: 'enterprise' },
    { key: 'api_access', label: 'API Access', plan: 'enterprise' },
  ]},
  { label: 'Advanced', features: [
    { key: 'ai_assistants', label: 'AI Assistants', plan: 'starter' },
    { key: 'property_wizard', label: 'Property Wizard', plan: 'starter' },
    { key: 'tools_hub', label: 'Tools Hub', plan: 'starter' },
    { key: 'ai_features', label: 'AI Features (Advanced)', plan: 'professional' },
    { key: 'benchmarking', label: 'Industry Benchmarks', plan: 'enterprise' },
    { key: 'financial_hub', label: 'Financial Hub', plan: 'enterprise' },
    { key: 'bulk_operations', label: 'Bulk Operations', plan: 'enterprise' },
    { key: 'white_label', label: 'White Label', plan: 'enterprise' },
  ]},
];

const PLAN_LABELS = { starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' };
const PLAN_COLORS = { starter: 'text-slate-500', professional: 'text-blue-600', enterprise: 'text-purple-600' };

export default function TenantManagement({ token }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', slug: '' });
  const [saving, setSaving] = useState(false);
  const [features, setFeatures] = useState({});
  const [activeSection, setActiveSection] = useState('overview');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/tenants`, { headers });
      setTenants(res.data.tenants || []);
    } catch { toast.error('Failed to load tenants'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const filtered = tenants.filter(t =>
    t.name?.toLowerCase().includes(search.toLowerCase()) ||
    t.slug?.toLowerCase().includes(search.toLowerCase())
  );

  const selectTenant = async (tenant) => {
    setSelectedTenant(tenant);
    setActiveSection('overview');
    try {
      const switchRes = await axios.post(`${BACKEND_URL}/api/admin/tenants/${tenant.id}/switch`, {}, { headers });
      const featRes = await axios.get(`${BACKEND_URL}/api/tenant-features`, { headers: { Authorization: `Bearer ${switchRes.data.token}` } });
      setFeatures(featRes.data || {});
    } catch { /* features may not load for some tenants */ }
  };

  const openCreate = () => {
    setEditForm({ name: '', slug: '' });
    setEditDialog(true);
  };

  const openEdit = (t) => {
    setEditForm({ name: t.name, slug: t.slug || '' });
    setEditDialog(true);
  };

  const saveTenant = async () => {
    setSaving(true);
    try {
      if (selectedTenant) {
        await axios.put(`${BACKEND_URL}/api/admin/tenants/${selectedTenant.id}`, editForm, { headers });
        toast.success('Tenant updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/tenants`, editForm, { headers });
        toast.success('Tenant created');
      }
      setEditDialog(false);
      fetchTenants();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const deleteTenant = async (id) => {
    if (!window.confirm('Delete this tenant? All of its properties, users and data will be permanently removed.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/tenants/${id}`, { headers });
      toast.success('Tenant deleted');
      if (selectedTenant?.id === id) setSelectedTenant(null);
      fetchTenants();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/tenants/bulk-delete`, { tenant_ids: selectedIds }, { headers });
      toast.success(`Deleted ${res.data.count} tenant${res.data.count === 1 ? '' : 's'}`);
      if (selectedIds.includes(selectedTenant?.id)) setSelectedTenant(null);
      setSelectedIds([]);
      setBulkDialog(false);
      fetchTenants();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk delete failed'); }
    finally { setBulkDeleting(false); }
  };

  const toggleFeature = (key, planTier) => {
    const isOn = features[key] ?? false;
    if (isOn) {
      // Turning OFF - show caution if it's part of their plan
      const tenantPlan = selectedTenant?.plan || 'starter';
      const planFeatures = { starter: FEATURE_GROUPS.flatMap(g => g.features.filter(f => f.plan === 'starter').map(f => f.key)),
        professional: FEATURE_GROUPS.flatMap(g => g.features.filter(f => ['starter', 'professional'].includes(f.plan)).map(f => f.key)),
        enterprise: FEATURE_GROUPS.flatMap(g => g.features).map(f => f.key) };
      const inPlan = (planFeatures[tenantPlan] || []).includes(key);
      if (inPlan && !window.confirm(`This feature is included in the ${PLAN_LABELS[tenantPlan] || tenantPlan} plan. Disabling it will hide it from this tenant. Continue?`)) {
        return;
      }
    }
    setFeatures(prev => ({ ...prev, [key]: !isOn }));
  };

  const saveFeatures = async () => {
    if (!selectedTenant) return;
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/tenants/${selectedTenant.id}/features`, features, { headers });
      toast.success('Features saved');
    } catch { toast.error('Failed to save features'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="flex flex-col md:flex-row h-full" data-testid="tenant-management">
      {/* Tenant List */}
      <div className={`md:w-72 border-b md:border-b-0 md:border-r bg-muted/20 flex flex-col shrink-0 ${selectedTenant ? 'hidden md:flex' : ''}`}>
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Tenants</h3>
            <Button variant="outline" size="sm" onClick={openCreate} data-testid="create-tenant-btn" className="h-7 gap-1 text-xs">
              <Plus className="h-3 w-3" /> New
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Search tenants..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs" data-testid="tenant-search" />
          </div>
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSelectedIds(selectedIds.length === filtered.length ? [] : filtered.map(t => t.id))}
              className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
              data-testid="tenant-select-all">
              {selectedIds.length === filtered.length && filtered.length > 0 ? 'Clear selection' : 'Select all'}
            </button>
            {selectedIds.length > 0 && (
              <Button variant="destructive" size="sm" className="h-6 gap-1 text-[10px] px-2"
                onClick={() => setBulkDialog(true)} data-testid="bulk-delete-btn">
                <Trash2 className="h-3 w-3" /> Delete ({selectedIds.length})
              </Button>
            )}
          </div>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {filtered.map(t => (
              <div key={t.id} className={`flex items-start gap-1.5 p-1 rounded-lg ${selectedIds.includes(t.id) ? 'bg-destructive/5' : ''}`}>
                <Checkbox
                  checked={selectedIds.includes(t.id)}
                  onCheckedChange={() => toggleSelect(t.id)}
                  className="mt-3 ml-1 shrink-0"
                  data-testid={`tenant-check-${t.id}`}
                />
                <button onClick={() => selectTenant(t)} data-testid={`tenant-${t.id}`}
                  className={`flex-1 min-w-0 text-left p-2 rounded-lg transition-colors text-sm ${
                    selectedTenant?.id === t.id ? 'bg-primary/10 border border-primary/20' : 'hover:bg-muted/60'
                  }`}>
                  <div className="font-medium truncate">{t.name}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <Building2 className="h-3 w-3" />{t.property_count || 0}
                    </span>
                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                      <Users className="h-3 w-3" />{t.user_count || 0}
                    </span>
                    {t.slug && <Badge variant="outline" className="text-[9px] h-4">{t.slug}</Badge>}
                  </div>
                </button>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-6">No tenants found</p>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Detail Panel */}
      <div className="flex-1 overflow-hidden">
        {selectedTenant ? (
          <div className="h-full flex flex-col">
            {/* Tenant Header */}
            <div className="p-4 md:p-5 border-b">
              <button onClick={() => setSelectedTenant(null)} className="md:hidden text-xs text-primary flex items-center gap-1 mb-2">
                <ChevronRight className="h-3 w-3 rotate-180" /> Back to tenants
              </button>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>{selectedTenant.name}</h2>
                  <div className="flex items-center gap-3 mt-1">
                    {selectedTenant.slug && <Badge variant="secondary" className="text-xs"><Globe className="h-3 w-3 mr-1" />{selectedTenant.slug}</Badge>}
                    <span className="text-xs text-muted-foreground flex items-center gap-1"><Building2 className="h-3 w-3" />{selectedTenant.property_count || 0} properties</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" />{selectedTenant.user_count || 0} users</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEdit(selectedTenant)} className="gap-1 text-xs">
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => deleteTenant(selectedTenant.id)} className="gap-1 text-xs text-destructive hover:text-destructive">
                    <Trash2 className="h-3 w-3" /> Delete
                  </Button>
                </div>
              </div>
              {/* Section tabs */}
              <div className="flex gap-1 mt-4 bg-muted/60 rounded-lg p-0.5 w-fit">
                {[
                  { id: 'overview', label: 'Overview', icon: Building2 },
                  { id: 'features', label: 'Features', icon: ToggleLeft },
                ].map(s => (
                  <button key={s.id} onClick={() => setActiveSection(s.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      activeSection === s.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}>
                    <s.icon className="h-3.5 w-3.5" /> {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1 p-5">
              {activeSection === 'overview' ? (
                <div className="space-y-6 max-w-2xl">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg border bg-card">
                      <div className="text-2xl font-bold">{selectedTenant.property_count || 0}</div>
                      <div className="text-xs text-muted-foreground mt-1">Properties</div>
                    </div>
                    <div className="p-4 rounded-lg border bg-card">
                      <div className="text-2xl font-bold">{selectedTenant.user_count || 0}</div>
                      <div className="text-xs text-muted-foreground mt-1">Users</div>
                    </div>
                    <div className="p-4 rounded-lg border bg-card">
                      <div className="text-2xl font-bold">{Object.values(features).filter(Boolean).length}</div>
                      <div className="text-xs text-muted-foreground mt-1">Active Features</div>
                    </div>
                  </div>
                  <div className="rounded-lg border bg-card p-4 space-y-3">
                    <h4 className="text-sm font-semibold">Details</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-muted-foreground text-xs">ID</span><p className="font-mono text-xs">{selectedTenant.id}</p></div>
                      <div><span className="text-muted-foreground text-xs">Slug</span><p>{selectedTenant.slug || '-'}</p></div>
                      <div className="col-span-2"><span className="text-muted-foreground text-xs">Organization Login URL</span><p className="font-mono text-xs mt-0.5 p-2 bg-muted/40 rounded">{window.location.origin}/org/{selectedTenant.slug || selectedTenant.id}</p></div>
                      <div><span className="text-muted-foreground text-xs">Created</span><p className="text-xs">{selectedTenant.created_at ? new Date(selectedTenant.created_at).toLocaleDateString() : '-'}</p></div>
                    </div>
                  </div>
                </div>
              ) : activeSection === 'features' ? (
                <div className="space-y-5 max-w-2xl">
                  {FEATURE_GROUPS.map(group => (
                    <div key={group.label}>
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{group.label}</h4>
                      <div className="space-y-1">
                        {group.features.map(f => {
                          const isOn = features[f.key] ?? false;
                          const tenantPlan = selectedTenant?.plan || 'starter';
                          const isInPlan = f.plan === 'starter' || (f.plan === 'professional' && ['professional', 'enterprise'].includes(tenantPlan)) || (f.plan === 'enterprise' && tenantPlan === 'enterprise');
                          return (
                          <div key={f.key} className={`flex items-center justify-between p-2.5 rounded-lg border ${isOn ? 'bg-card' : 'bg-muted/20'}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{f.label}</span>
                              <Badge variant="outline" className={`text-[8px] ${PLAN_COLORS[f.plan] || ''}`}>
                                {PLAN_LABELS[f.plan] || f.plan}
                              </Badge>
                              {isInPlan && !isOn && (
                                <Badge variant="outline" className="text-[8px] text-amber-600 border-amber-300">Plan included - disabled by admin</Badge>
                              )}
                            </div>
                            <Switch checked={isOn} onCheckedChange={() => toggleFeature(f.key, f.plan)} />
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <Button onClick={saveFeatures} disabled={saving} className="gap-1.5">
                    <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save Features'}
                  </Button>
                </div>
              ) : null}
            </ScrollArea>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Building2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a tenant to manage</p>
              <p className="text-xs mt-1">or create a new one</p>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Delete Confirm Dialog */}
      <Dialog open={bulkDialog} onOpenChange={setBulkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" /> Delete {selectedIds.length} Tenant{selectedIds.length === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              This permanently deletes each tenant along with <strong>all of its properties, users, assignments, invoices and usage data</strong>. This cannot be undone.
            </p>
            <div className="border rounded-lg divide-y max-h-52 overflow-y-auto">
              {tenants.filter(t => selectedIds.includes(t.id)).map(t => (
                <div key={t.id} className="flex items-center gap-2 p-2 text-xs">
                  <span className="font-medium flex-1 truncate">{t.name}</span>
                  <span className="text-muted-foreground shrink-0">{t.property_count || 0} props · {t.user_count || 0} users</span>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={bulkDelete} disabled={bulkDeleting} className="gap-1.5" data-testid="confirm-bulk-delete-btn">
              {bulkDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedTenant && editForm.name ? 'Edit Tenant' : 'Create Tenant'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                placeholder="Coastal Hospitality Group" className="mt-1" data-testid="tenant-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Slug</label>
              <Input value={editForm.slug} onChange={e => setEditForm(p => ({ ...p, slug: e.target.value }))}
                placeholder="coastal-hospitality" className="mt-1" data-testid="tenant-slug-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={saveTenant} disabled={saving || !editForm.name}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {selectedTenant && editForm.name ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
